import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, CaretRight, Users, Wallet, Megaphone, IdentificationCard, ChatCircleDots, Images, BookOpen, Broadcast, MagnifyingGlass, CalendarCheck, FileXls, CheckCircle, RadioButton, Eye, Money, PaperPlaneTilt, Receipt } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { wibNow } from "@/lib/time";
import { useAuth } from "@/context/AuthContext";

const DAYS = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jum'at", "Sabtu"];
const MON = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const GROUPS = ["TK B", "TK A", "KB"];
const SD_GROUPS = ["Kelas 1", "Kelas 2", "Kelas 3", "Kelas 4", "Kelas 5", "Kelas 6"];

const ACTIONS = [
  { key: "siswa", label: "Data Siswa", icon: IdentificationCard, path: "/kelola/siswa", color: "bg-sky-100 text-sky-600" },
  { key: "users", label: "Guru & Ortu", icon: Users, path: "/kelola/users", color: "bg-purple-100 text-purple-600" },
  { key: "spp", label: "Kelola SPP", icon: Wallet, path: "/kelola/spp", color: "bg-orange-100 text-orange-600" },
  { key: "absensi-guru", label: "Kehadiran Guru", icon: CheckCircle, path: "/kelola/laporan-guru", color: "bg-teal-100 text-teal-600" },
  { key: "mukafaah", label: "Mukafaah Guru", icon: Money, path: "/kelola/mukafaah", color: "bg-lime-100 text-lime-700" },
  { key: "kwitansi", label: "Kwitansi", icon: Receipt, path: "/kelola/kwitansi", color: "bg-amber-100 text-amber-700" },
  { key: "kirim-pesan", label: "Kirim Pesan Guru", icon: PaperPlaneTilt, path: "/kelola/kirim-pesan", color: "bg-blue-100 text-blue-600" },
  { key: "jadwal", label: "Jadwal", icon: CalendarCheck, path: "/kelola/jadwal", color: "bg-rose-100 text-rose-600" },
  { key: "pengumuman", label: "Pengumuman", icon: Megaphone, path: "/info", color: "bg-rose-100 text-rose-600" },
  { key: "galeri", label: "Update Galeri", icon: Images, path: "/galeri", color: "bg-fuchsia-100 text-fuchsia-600" },
  { key: "konten", label: "Kelola Konten Hafalan", icon: BookOpen, path: "/kelola/konten", color: "bg-indigo-100 text-indigo-600" },
  { key: "kajian-radio", label: "Kelola Kajian & Radio", icon: RadioButton, path: "/kelola/kajian-radio", color: "bg-indigo-100 text-indigo-600" },
  { key: "info-page", label: "Kelola Halaman Informasi", icon: Megaphone, path: "/kelola/informasi", color: "bg-amber-100 text-amber-600" },
  { key: "monitor", label: "Monitor Pengguna", icon: Broadcast, path: "/kelola/monitor", color: "bg-cyan-100 text-cyan-600" },
  { key: "chat", label: "Pesan", icon: ChatCircleDots, path: "/pesan", color: "bg-green-100 text-green-600" },
  { key: "export", label: "Ekspor Data", icon: FileXls, path: "/kelola/export", color: "bg-emerald-100 text-emerald-600" },
];

export default function AdminHome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSdqu = user?.school === "sdqu";
  const [ov, setOv] = useState(null);
  const [err, setErr] = useState(false);
  const [now, setNow] = useState(wibNow());
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [parents, setParents] = useState([]);
  const [q, setQ] = useState("");
  const [visits, setVisits] = useState(null);
  const [gbPending, setGbPending] = useState(0);
  const [msgResp, setMsgResp] = useState(0);

  useEffect(() => { const t = setInterval(() => setNow(wibNow()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => { api.get("/admin/attendance-overview").then((r) => setOv(r.data)).catch(() => setErr(true)); }, []);
  useEffect(() => {
    api.get("/students").then((r) => setStudents(r.data)).catch(() => {});
    api.get("/users?role=guru").then((r) => setTeachers(r.data)).catch(() => {});
    api.get("/admin/parents").then((r) => setParents(r.data)).catch(() => {});
  }, []);
  useEffect(() => { api.get("/info-visits").then((r) => setVisits(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/admin/guestbook").then((r) => setGbPending(r.data.pending)).catch(() => {}); }, []);
  useEffect(() => { api.get("/admin/guru-messages/unseen-count").then((r) => setMsgResp(r.data.count)).catch(() => {}); }, []);

  const term = q.trim().toLowerCase();
  const studentResults = term ? students.filter((s) => (s.name || "").toLowerCase().includes(term)).slice(0, 6) : [];
  const teacherResults = term ? teachers.filter((t) => (t.name || "").toLowerCase().includes(term)).slice(0, 6) : [];
  const parentSeen = new Set();
  const parentResults = term ? parents.filter((p) => {
    if (!p.parent_id || parentSeen.has(p.parent_id)) return false;
    const hit = (p.parent_name || "").toLowerCase().includes(term) || (p.student_name || "").toLowerCase().includes(term);
    if (hit) { parentSeen.add(p.parent_id); return true; }
    return false;
  }).slice(0, 6) : [];
  const hasResults = studentResults.length || teacherResults.length || parentResults.length;

  const clock = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}:${String(now.getSeconds()).padStart(2, "0")}`;

  return (
    <div className="pb-4">
      {/* Header */}
      <div className="bg-primary text-white px-5 pt-7 pb-8 rounded-b-[2rem] relative overflow-hidden">
        <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/10 rounded-full" />
        <div className="flex items-center gap-3 relative">
          <div className="w-12 h-12 rounded-2xl bg-white/95 flex items-center justify-center shadow-md shrink-0 overflow-hidden p-1">
            <img src={isSdqu ? "/sdqu_logo.png" : "/tk_logo.png"} alt="Muslim Kids" className="w-full h-full object-contain" />
          </div>
          <div>
            <p className="font-heading text-lg font-bold leading-tight">Ahlan wa Sahlan, Admin 👋</p>
            <p className="text-white/80 text-xs">Selamat datang kembali</p>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3 relative">
          <div data-testid="admin-clock" className="bg-white/20 rounded-full px-2.5 py-1 text-xs font-bold flex items-center gap-1">
            <Clock size={13} weight="fill" /> {clock} WIB
          </div>
          <span className="text-white/85 text-xs">{DAYS[now.getDay()]}, {now.getDate()} {MON[now.getMonth()]} {now.getFullYear()}</span>
        </div>
      </div>

      <div className="px-5 -mt-4 relative space-y-4">
        {gbPending > 0 && (
          <button data-testid="admin-gb-notif" onClick={() => navigate("/kelola/informasi")}
            className="w-full bg-red-50 border border-red-200 rounded-2xl px-4 py-3 flex items-center gap-3 shadow-sm active:scale-[0.99] transition-transform text-left">
            <div className="w-10 h-10 rounded-xl bg-red-100 text-red-600 flex items-center justify-center shrink-0 relative">
              <Megaphone size={20} weight="duotone" />
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{gbPending}</span>
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-red-700 text-sm">{gbPending} pesan Buku Tamu baru</p>
              <p className="text-red-500/80 text-xs">Menunggu persetujuan — ketuk untuk meninjau</p>
            </div>
            <CaretRight size={18} className="text-red-300 shrink-0 ml-auto" />
          </button>
        )}
        {/* Search siswa */}
        <div className="relative" data-testid="admin-search-siswa">
          <div className="relative">
            <MagnifyingGlass size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" />
            <input data-testid="admin-search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Cari siswa, guru, atau orang tua..."
              className="w-full bg-white border border-stone-200/70 rounded-2xl py-3.5 pl-11 pr-4 text-sm shadow-sm outline-none focus:border-primary" />
          </div>
          {term && (
            <div className="mt-2 bg-white rounded-2xl border border-stone-200/60 shadow-lg overflow-hidden max-h-[60vh] overflow-y-auto" data-testid="admin-search-results">
              {!hasResults && <p className="text-stone-400 text-sm p-4 text-center">Tidak ada hasil untuk "{q}".</p>}
              {studentResults.length > 0 && (
                <div data-testid="search-cat-siswa">
                  <p className="px-4 pt-3 pb-1 text-[11px] font-bold text-sky-600 uppercase tracking-wide">Siswa</p>
                  {studentResults.map((s) => (
                    <button key={s.id} data-testid={`search-result-${s.id}`} onClick={() => navigate(`/siswa/${s.id}`)}
                      className="w-full flex items-center justify-between gap-2 px-4 py-2.5 border-b border-stone-50 last:border-0 active:bg-stone-50 transition-colors text-left">
                      <div><p className="font-semibold text-stone-800 text-sm">{s.name}</p>
                        <p className="text-stone-400 text-xs">{s.kelas || "—"}{s.teacher_name ? ` • Umi ${s.teacher_name.replace(/^Umi\s+/i, "")}` : ""}</p></div>
                      <CaretRight size={16} className="text-stone-300 shrink-0" />
                    </button>
                  ))}
                </div>
              )}
              {teacherResults.length > 0 && (
                <div data-testid="search-cat-guru">
                  <p className="px-4 pt-3 pb-1 text-[11px] font-bold text-primary uppercase tracking-wide">Guru</p>
                  {teacherResults.map((t) => (
                    <button key={t.id} data-testid={`search-guru-${t.id}`} onClick={() => navigate(`/kelola/guru-detail/${t.id}`)}
                      className="w-full flex items-center justify-between gap-2 px-4 py-2.5 border-b border-stone-50 last:border-0 active:bg-stone-50 transition-colors text-left">
                      <div><p className="font-semibold text-stone-800 text-sm">{t.name}</p>
                        <p className="text-stone-400 text-xs">{t.kelas || "—"}{t.phone ? ` • ${t.phone}` : ""}</p></div>
                      <CaretRight size={16} className="text-stone-300 shrink-0" />
                    </button>
                  ))}
                </div>
              )}
              {parentResults.length > 0 && (
                <div data-testid="search-cat-ortu">
                  <p className="px-4 pt-3 pb-1 text-[11px] font-bold text-fuchsia-600 uppercase tracking-wide">Orang Tua</p>
                  {parentResults.map((p) => (
                    <button key={p.parent_id} data-testid={`search-ortu-${p.parent_id}`} onClick={() => navigate(`/kelola/ortu/${p.parent_id}`)}
                      className="w-full flex items-center justify-between gap-2 px-4 py-2.5 border-b border-stone-50 last:border-0 active:bg-stone-50 transition-colors text-left">
                      <div><p className="font-semibold text-stone-800 text-sm">{p.parent_name || `Orang Tua ${p.student_name}`}</p>
                        <p className="text-stone-400 text-xs">Ortu {p.student_name} • {p.kelas || "—"}{p.phone ? ` • ${p.phone}` : ""}</p></div>
                      <CaretRight size={16} className="text-stone-300 shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {err && <p className="text-center text-red-500 text-sm bg-white rounded-2xl border border-red-100 py-3">Gagal memuat data kehadiran. Coba muat ulang.</p>}
        <RekapCard kind="guru" title="👩‍🏫 Kehadiran Guru Hari Ini" data={ov?.teachers} navigate={navigate} isSdqu={isSdqu} />
        <RekapCard kind="murid" title="🎒 Kehadiran Murid Hari Ini" data={ov?.students} navigate={navigate} isSdqu={isSdqu} />
        <InfoVisitsCard visits={visits} />
      </div>

      {/* Quick actions */}
      <div className="px-5 mt-6">
        <h3 className="font-heading font-semibold text-stone-800 mb-3">Kelola Sistem</h3>
        <div className="grid grid-cols-3 gap-3">
          {ACTIONS.map((a) => {
            const Icon = a.icon;
            return (
              <button key={a.key} data-testid={`admin-action-${a.key}`} onClick={() => navigate(a.path)}
                className="relative bg-white rounded-2xl border border-stone-200/60 p-3 flex flex-col items-center gap-2 shadow-sm active:scale-95 transition-transform">
                {a.key === "kirim-pesan" && msgResp > 0 && <span data-testid="kirim-pesan-badge" className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center">{msgResp}</span>}
                <div className={`w-11 h-11 rounded-xl ${a.color} flex items-center justify-center`}><Icon size={22} weight="duotone" /></div>
                <span className="text-[10px] font-semibold text-stone-600 text-center leading-tight">{a.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function InfoVisitsCard({ visits }) {
  const MONS = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
  const MONS_FULL = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
  const fmt = (iso) => { const [y, m, d] = (iso || "").split("-"); return d ? `${parseInt(d, 10)} ${MONS[parseInt(m, 10) - 1]}` : iso; };
  const w = wibNow();
  const curY = w.getFullYear();
  const curM = w.getMonth() + 1;
  const monthTotal = (visits?.days || [])
    .filter((d) => { const [y, m] = (d.date || "").split("-"); return parseInt(y, 10) === curY && parseInt(m, 10) === curM; })
    .reduce((s, d) => s + d.count, 0);
  const days = (visits?.days || []).slice(0, 3);
  return (
    <div className="bg-white rounded-3xl border border-stone-200/60 shadow-lg p-4" data-testid="info-visits-card">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-xl bg-violet-100 text-violet-600 flex items-center justify-center"><Eye size={20} weight="duotone" /></div>
        <h3 className="font-heading font-semibold text-stone-800">Pengunjung Halaman Informasi</h3>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="bg-violet-50 rounded-2xl p-3 text-center">
          <p className="font-heading font-bold text-2xl text-violet-600" data-testid="info-visits-today">{visits?.today ?? 0}</p>
          <p className="text-[11px] text-stone-500">Hari Ini</p>
        </div>
        <div className="bg-stone-50 rounded-2xl p-3 text-center">
          <p className="font-heading font-bold text-2xl text-stone-700" data-testid="info-visits-total">{monthTotal}</p>
          <p className="text-[11px] text-stone-500">Total ({MONS_FULL[curM - 1]})</p>
        </div>
      </div>
      {days.length > 0 ? (
        <div className="space-y-1.5" data-testid="info-visits-list">
          {days.map((d) => (
            <div key={d.date} className="flex items-center justify-between bg-stone-50 rounded-xl px-3 py-2">
              <span className="font-semibold text-stone-700 text-sm">{fmt(d.date)}</span>
              <span className="text-sm font-bold text-violet-600">{d.count} <span className="text-stone-400 font-medium">pengunjung</span></span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-stone-400 text-sm text-center py-2">Belum ada kunjungan tercatat.</p>
      )}
    </div>
  );
}

function RekapCard({ kind, title, data, navigate, isSdqu }) {
  const groups = isSdqu ? SD_GROUPS : GROUPS;
  return (
    <div className="bg-white rounded-3xl border border-stone-200/60 shadow-lg p-4" data-testid={`rekap-${kind}`}>
      <button data-testid={`rekap-${kind}-open`} onClick={() => navigate(`/admin/kehadiran/${kind}`)}
        className="w-full flex items-center justify-between mb-3 active:scale-[0.99] transition-transform">
        <h3 className="font-heading font-semibold text-stone-800 text-left">{title}</h3>
        <CaretRight size={18} className="text-stone-400 shrink-0" />
      </button>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="bg-green-50 rounded-2xl p-3 text-center">
          <p className="font-heading font-bold text-2xl text-green-600" data-testid={`rekap-${kind}-hadir`}>{data?.hadir ?? 0}</p>
          <p className="text-[11px] text-stone-500">Hadir</p>
        </div>
        <div className="bg-red-50 rounded-2xl p-3 text-center">
          <p className="font-heading font-bold text-2xl text-red-500" data-testid={`rekap-${kind}-tidak`}>{data?.tidak_hadir ?? 0}</p>
          <p className="text-[11px] text-stone-500">Tidak Hadir</p>
        </div>
      </div>
      <div className="space-y-1.5">
        {groups.map((g) => {
          const grp = data?.groups?.[g] || { hadir: 0, total: 0 };
          return (
            <button key={g} data-testid={`rekap-${kind}-grup-${g}`} onClick={() => navigate(`/admin/kehadiran/${kind}/grup/${encodeURIComponent(g)}`)}
              className="w-full flex items-center justify-between bg-stone-50 rounded-xl px-3 py-2.5 active:scale-[0.98] transition-transform">
              <span className="font-semibold text-stone-700 text-sm">{g}</span>
              <span className="flex items-center gap-2">
                <span className="text-sm font-bold text-primary">{grp.hadir}<span className="text-stone-400 font-medium">/{grp.total}</span></span>
                <CaretRight size={16} className="text-stone-300" />
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
